import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { StatusBadge, PriorityBadge, TicketIdBadge } from "@/components/StatusBadge";
import SLABadge from "@/components/SLABadge";
import { AlertTriangle, TrendingUp, Ticket as TicketIcon, CheckCircle2, Timer, Star, PhoneCall } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { useT } from "@/lib/i18n";
import { normalizeLayout } from "@/lib/dashboardBlocks";
import { AgentLeaderboard } from "@/components/AgentLeaderboard";
import { AgentOfMonth } from "@/components/AgentOfMonth";

const STATUS_COLORS = {
  new: "#dc2626", open: "#ef4444", assigned: "#f87171", in_progress: "#fca5a5",
  pending_customer: "#fecaca", pending_third_party: "#fed7aa",
  resolved: "#0f172a", closed: "#334155", cancelled: "#94a3b8", reopened: "#7f1d1d",
};

const fmtMin = (m) => m ? (m > 60 ? `${(m/60).toFixed(1)}h` : `${Math.round(m)}m`) : "—";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([api.get("/dashboard/stats"), api.get("/tickets")]);
        setStats(s.data);
        setRecent(r.data.slice(0, 8));
      } catch { /* noop */ }
    })();
  }, []);

  if (!stats) return <div className="text-slate-500 font-mono text-xs uppercase tracking-[0.3em]">{t("dash_loading")}</div>;

  const statusData = Object.entries(stats.status_counts).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v, key: k }));
  const priorityData = Object.entries(stats.priority_counts).map(([k, v]) => ({ name: k, value: v }));
  const categoryData = Object.entries(stats.category_counts).slice(0, 8).map(([k, v]) => ({ name: k.length > 18 ? k.slice(0,18)+"…" : k, value: v }));
  const assigneeData = Object.entries(stats.assignee_counts).slice(0, 8).map(([k, v]) => ({ name: k.length > 18 ? k.slice(0,18)+"…" : k, value: v }));

  const openCount = (stats.status_counts.new || 0) + (stats.status_counts.open || 0) + (stats.status_counts.assigned || 0) + (stats.status_counts.in_progress || 0);
  const pendingCount = (stats.status_counts.pending_customer || 0) + (stats.status_counts.pending_third_party || 0);
  const doneCount = (stats.status_counts.resolved || 0) + (stats.status_counts.closed || 0);
  const layout = normalizeLayout(user.dashboard_layout);
  const ord = (id) => layout.findIndex((b) => b.id === id);
  const vis = (id) => layout.find((b) => b.id === id)?.visible !== false;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 grid-flow-dense" data-testid="dashboard-grid">
      <div className="flex items-end justify-between flex-wrap gap-4 lg:col-span-3" style={{ order: -1 }}>
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600" data-testid="dashboard-heading">{t("dash_command_center")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2">{t("dash_hello", { name: user.name.split(" ")[0] })}</h1>
          <p className="text-slate-600 mt-2 text-sm max-w-lg">{t("dash_intro")}</p>
        </div>
        <Link to="/tickets/new">
          <button data-testid="dashboard-create-btn" className="bg-red-600 hover:bg-red-500 text-white px-5 h-11 rounded-sm text-sm font-medium">+ {t("new_ticket")}</button>
        </Link>
      </div>

      {/* KPIs */}
      {vis("kpis") && <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 border border-slate-200" style={{ order: ord("kpis") }} data-testid="dash-block-kpis">
        <Kpi label={t("kpi_total")} value={stats.total} icon={<TicketIcon className="h-4 w-4" strokeWidth={1.5} />} testid="kpi-total" />
        <Kpi label={t("kpi_open")} value={openCount} icon={<TrendingUp className="h-4 w-4 text-red-600" strokeWidth={1.5} />} testid="kpi-open" />
        <Kpi label={t("kpi_pending")} value={pendingCount} icon={<Timer className="h-4 w-4 text-orange-400" strokeWidth={1.5} />} testid="kpi-pending" />
        <Kpi label={t("kpi_resolved")} value={doneCount} icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />} testid="kpi-resolved" />
        <Kpi label={t("kpi_sla_breached")} value={stats.sla_breached} accent={stats.sla_breached > 0 ? "text-red-400" : ""} icon={<AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={1.5} />} testid="kpi-breached" />
        <Kpi label={t("kpi_csat")} value={stats.csat_avg ? `${stats.csat_avg}★` : "—"} sub={stats.csat_count ? t("kpi_ratings_suffix", { n: stats.csat_count }) : ""} icon={<Star className="h-4 w-4 text-amber-400" strokeWidth={1.5} />} testid="kpi-csat" />
        <Kpi label={t("kpi_low_ratings")} value={stats.csat_low_count || 0} sub={stats.csat_pending_count ? t("kpi_awaiting_reply", { n: stats.csat_pending_count }) : t("kpi_at_most_2")} accent={stats.csat_low_count > 0 ? "text-red-400" : ""} icon={<AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={1.5} />} testid="kpi-csat-low" />
      </div>}

      {/* CSAT low-rating drill-down (staff-visible when any) */}
      {vis("low_ratings") && stats.csat_low_tickets && stats.csat_low_tickets.length > 0 && (
        <div className="lg:col-span-3 border border-red-500/30 bg-red-500/5" style={{ order: ord("low_ratings") }} data-testid="csat-low-panel">
          <div className="p-4 border-b border-red-500/30 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={1.5} />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-300">{t("low_ratings_header")}</div>
          </div>
          <div className="divide-y divide-red-500/20">
            {stats.csat_low_tickets.map((tk) => (
              <Link key={tk.ticket_id} to={`/tickets/${tk.ticket_id}`} className="flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-2 px-4 md:px-5 py-3 hover:bg-red-500/10" data-testid={`csat-low-row-${tk.ticket_number}`}>
                <TicketIdBadge id={tk.ticket_number} />
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map((n) => (<Star key={n} className={`h-3.5 w-3.5 ${n <= tk.rating ? "text-amber-400 fill-amber-400" : "text-slate-700"}`} strokeWidth={1.5} />))}
                </div>
                <div className="flex-1 basis-full md:basis-auto min-w-0">
                  <div className="text-sm truncate">{tk.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono truncate">{tk.reporter_name}{tk.assignee_name ? ` · ${tk.assignee_name}` : ""}{tk.comment ? ` · "${tk.comment.slice(0, 80)}${tk.comment.length > 80 ? '…' : ''}"` : ""}</div>
                </div>
                {tk.followup_ticket_id ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border rounded-sm bg-amber-500/15 text-amber-300 border-amber-500/40" data-testid={`callback-badge-${tk.ticket_number}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/tickets/${tk.followup_ticket_id}`; }}>
                    <PhoneCall className="h-3 w-3" strokeWidth={2} /> {t("callback_assigned")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border rounded-sm bg-red-500/15 text-red-300 border-red-500/40">
                    <AlertTriangle className="h-3 w-3" strokeWidth={2} /> {t("needs_callback")}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Speed metrics */}
      {vis("speed") && <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ order: ord("speed") }} data-testid="dash-block-speed">
        <MetricCard label={t("avg_first_response")} value={fmtMin(stats.avg_response_min)} description="Time from ticket created to first staff reply" />
        <MetricCard label={t("avg_resolution")} value={fmtMin(stats.avg_resolution_min)} description="Time from ticket created to resolved" />
      </div>}

      {/* Charts row 1 */}
        {vis("agent_of_month") && user.role !== "end_user" && <div className="lg:col-span-3" style={{ order: ord("agent_of_month") }} data-testid="dash-block-agent_of_month"><AgentOfMonth /></div>}
        {vis("leaderboard") && user.role !== "end_user" && <div className="lg:col-span-3" style={{ order: ord("leaderboard") }} data-testid="dash-block-leaderboard"><AgentLeaderboard /></div>}
        {vis("trend") && <div className="lg:col-span-2 border border-slate-200 bg-white p-6" style={{ order: ord("trend") }} data-testid="dash-block-trend">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Trend · Last 7 Days</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.trend}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 4, color: "#0f172a" }} labelStyle={{ color: "#e2e8f0" }} />
                <Line type="monotone" dataKey="created" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Created" />
                <Line type="monotone" dataKey="resolved" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} name="Resolved" />
                <Legend wrapperStyle={{ fontSize: 11, color: "#64748b" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>}

        {vis("status") && <div className="border border-slate-200 bg-white p-6" style={{ order: ord("status") }} data-testid="dash-block-status">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Tickets by Status</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={75} stroke="#ffffff" strokeWidth={2}>
                  {statusData.map((entry) => (<Cell key={entry.key} fill={STATUS_COLORS[entry.key] || "#64748b"} />))}
                </Pie>
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 4, color: "#0f172a" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
            {statusData.map((s) => (
              <div key={s.key} className="flex items-center justify-between text-[11px]">
                <div className="flex items-center gap-2 text-slate-600"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[s.key] }} /><span className="capitalize truncate">{s.name}</span></div>
                <span className="font-mono text-slate-700">{s.value}</span>
              </div>
            ))}
          </div>
        </div>}

      {/* Charts row 2 */}
        {vis("priority") && <div className="lg:col-span-1 border border-slate-200 bg-white p-6" style={{ order: ord("priority") }} data-testid="dash-block-priority">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">By Priority</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priorityData}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
                <Bar dataKey="value" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>}
        {vis("category") && <div className="lg:col-span-1 border border-slate-200 bg-white p-6" style={{ order: ord("category") }} data-testid="dash-block-category">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">By Category</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={10} width={110} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
                <Bar dataKey="value" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>}
        {vis("technician") && <div className="lg:col-span-1 border border-slate-200 bg-white p-6" style={{ order: ord("technician") }} data-testid="dash-block-technician">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">By Technician</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={assigneeData} layout="vertical">
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={10} width={110} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
                <Bar dataKey="value" fill="#64748b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>}

      {/* Recent */}
      {vis("recent") && <div className="lg:col-span-3 border border-slate-200 bg-white" style={{ order: ord("recent") }} data-testid="dash-block-recent">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Recent Tickets</div>
          <Link to="/tickets" className="text-xs text-red-600 hover:text-red-700" data-testid="dashboard-view-all">View all →</Link>
        </div>
        <div className="divide-y divide-slate-800">
          {recent.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No tickets yet.</div>}
          {recent.map((t) => (
            <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} className="flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-2 px-4 md:px-5 py-3 hover:bg-slate-100" data-testid={`recent-ticket-${t.ticket_id}`}>
              <TicketIdBadge id={t.ticket_number} />
              <div className="flex-1 basis-[calc(100%-8rem)] md:basis-auto min-w-0">
                <div className="text-sm truncate">{t.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono truncate">{t.category_name || "Uncategorized"} · {t.reporter_name}{t.organization ? ` · ${t.organization}` : ""}</div>
              </div>
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
              <SLABadge dueAt={t.resolution_due_at} status={t.status} responseDueAt={t.first_response_due_at} firstResponseAt={t.first_response_at} compact />
            </Link>
          ))}
        </div>
      </div>}
    </div>
  );
}

function Kpi({ label, value, sub, icon, accent, testid }) {
  return (
    <div className="p-5 border-r last:border-r-0 border-slate-200 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</div>
        {icon}
      </div>
      <div className={`mt-3 font-display font-black text-3xl tracking-tight ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-mono mt-1">{sub}</div>}
    </div>
  );
}

function MetricCard({ label, value, description }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</div>
      <div className="font-display font-black text-4xl mt-3">{value}</div>
      <div className="text-xs text-slate-500 mt-2">{description}</div>
    </div>
  );
}
